import { Mail, MapPin, Phone, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

export default function Contact() {
  useSEO({
    title: 'Contact Us | Get in Touch with GreenTick',
    description: 'Have questions about GreenTick? Contact our team to schedule a demo, start a trial, or discuss how our AI can transform your hiring process.',
    canonicalPath: '/contact',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8">
              <span>← Back to Home</span>
            </Link>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Get In
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                Touch
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              Ready to transform your hiring process? Let's connect and discuss how GreenTick can help you hire right and hire faster.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Email Us</h3>
                <a href="mailto:sandesh@greentick.org.in" className="text-slate-300 hover:text-teal-400 transition-colors">
                  sandesh@greentick.org.in
                </a>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Linkedin className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Connect on LinkedIn</h3>
                <a
                  href="https://www.linkedin.com/company/hirewithgreentick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-emerald-400 transition-colors"
                >
                  @hirewithgreentick
                </a>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Call Us</h3>
                <p className="text-slate-300">
                  Available Monday to Friday, 9 AM - 6 PM IST
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Location</h3>
                <p className="text-slate-300">
                  India
                </p>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">What Happens Next?</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-400 font-semibold">1</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">We'll Respond Within 24 Hours</h4>
                    <p className="text-slate-400">Our team will review your inquiry and get back to you quickly.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 font-semibold">2</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Schedule a Demo</h4>
                    <p className="text-slate-400">See GreenTick in action with a personalized demonstration.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-400 font-semibold">3</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Start Your Free Trial</h4>
                    <p className="text-slate-400">Experience the power of AI-driven recruitment firsthand.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:sandesh@greentick.org.in"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition-all hover:scale-105 flex items-center gap-2 justify-center"
              >
                <Mail className="w-5 h-5" />
                Send Email
              </a>
              <a
                href="https://dashboard.greentick.org.in/demo"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 glass text-white rounded-xl font-semibold hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center"
              >
                Try Demo
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
